import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Upload, Download, Copy, Trash2, File, Image as ImageIcon, FileText, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { getImageUrl } from '../utils/imageUrl';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFiles = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, images, documents

  useEffect(() => {
    fetchFiles();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/files`,
        getAuthHeaders()
      );
      setFiles(response.data.files);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      alert('Erro ao carregar arquivos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/files/upload`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { uploaded, errors, total_uploaded, total_errors } = response.data;

      if (total_uploaded > 0) {
        alert(`${total_uploaded} arquivo(s) enviado(s) com sucesso!`);
        fetchFiles();
      }

      if (total_errors > 0) {
        const errorMessages = errors.map(e => `${e.filename}: ${e.error}`).join('\n');
        alert(`Erros:\n${errorMessages}`);
      }
    } catch (error) {
      alert('Erro ao fazer upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${filename}"?`)) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/delete-image/${filename}`,
        getAuthHeaders()
      );
      alert('Arquivo excluído com sucesso!');
      fetchFiles();
    } catch (error) {
      alert('Erro ao excluir arquivo: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCopyUrl = (filename) => {
    // Gerar URL pública correta baseada no ambiente
    const publicUrl = getImageUrl(filename);
    
    // Se a URL já é absoluta, usar diretamente
    // Se é relativa, adicionar o domínio do site atual
    const fullUrl = publicUrl.startsWith('http') 
      ? publicUrl 
      : `${window.location.origin}${publicUrl}`;
    
    navigator.clipboard.writeText(fullUrl);
    alert('URL copiada para a área de transferência!');
  };

  const handleDownload = async (filename) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/files/download/${filename}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Erro ao baixar arquivo');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = 
      filterType === 'all' ? true :
      filterType === 'images' ? file.is_image :
      filterType === 'documents' ? !file.is_image : true;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Carregando arquivos...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center space-x-2 hover:text-[#00A86B]"
          >
            <ArrowLeft size={20} />
            <span>Voltar ao Dashboard</span>
          </Button>

          <Button
            onClick={() => document.getElementById('file-upload-input').click()}
            disabled={uploading}
            className="bg-[#00A86B] hover:bg-[#008c5a] text-white"
          >
            <Upload size={18} className="mr-2" />
            {uploading ? 'Enviando...' : 'Enviar Arquivos'}
          </Button>
          <input
            id="file-upload-input"
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>

        <Card className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Gerenciador de Arquivos</h1>
            <p className="text-gray-600">Gerencie todos os arquivos enviados no site</p>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Buscar arquivos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterType('all')}
                size="sm"
              >
                Todos ({files.length})
              </Button>
              <Button
                variant={filterType === 'images' ? 'default' : 'outline'}
                onClick={() => setFilterType('images')}
                size="sm"
              >
                Imagens ({files.filter(f => f.is_image).length})
              </Button>
              <Button
                variant={filterType === 'documents' ? 'default' : 'outline'}
                onClick={() => setFilterType('documents')}
                size="sm"
              >
                Documentos ({files.filter(f => !f.is_image).length})
              </Button>
            </div>
          </div>

          {/* Files Grid */}
          {filteredFiles.length === 0 ? (
            <div className="text-center py-12">
              <File size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Nenhum arquivo encontrado</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredFiles.map((file) => (
                <Card key={file.filename} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Preview */}
                  <div className="h-40 bg-gray-100 flex items-center justify-center">
                    {file.is_image ? (
                      <img
                        src={getImageUrl(file.filename)}
                        alt={file.filename}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <FileText size={48} className="text-gray-400" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <p className="font-medium text-sm text-gray-800 truncate mb-1" title={file.filename}>
                      {file.filename}
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>{formatFileSize(file.size)}</p>
                      <p>{formatDate(file.modified)}</p>
                    </div>

                    {/* Actions */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyUrl(file.filename)}
                        className="text-xs"
                      >
                        <Copy size={14} className="mr-1" />
                        Copiar URL
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(file.filename)}
                        className="text-xs"
                      >
                        <Download size={14} className="mr-1" />
                        Baixar
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(file.filename)}
                        className="text-xs col-span-2"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-gray-600">
              Mostrando {filteredFiles.length} de {files.length} arquivo(s)
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminFiles;
